import authService from './authService';
import { ProductCategory, ProductCategoryFormData, Product } from '../types/product';
import { ApiResponse } from '../types/common';

// Backend pagination envelope (snake_case) returned by RespondSuccessWithPagination.
export interface BackendPagination {
  page: number;
  page_size: number;
  total_items: number;
  total_pages: number;
}

interface ListResponse<T> {
  success: boolean;
  data: T[];
  pagination: BackendPagination;
}

class ProductCategoryService {
  private async getApiClient() {
    return authService.getApiClient();
  }

  /** List product categories with pagination. */
  async listCategories(
    page = 1,
    pageSize = 100,
    search?: string,
    includeInactive = false
  ): Promise<{ categories: ProductCategory[]; pagination: BackendPagination | null }> {
    const params = new URLSearchParams({
      page: page.toString(),
      page_size: pageSize.toString(),
    });
    if (search) params.append('search', search);
    if (includeInactive) params.append('include_inactive', 'true');

    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/product-categories?${params}`);
    if (!response.ok) {
      throw new Error('Falha ao carregar categorias de produtos');
    }
    const json: ListResponse<ProductCategory> = await response.json();
    return { categories: json.data ?? [], pagination: json.pagination ?? null };
  }

  async getCategory(id: number): Promise<ProductCategory> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/product-categories/${id}`);
    if (!response.ok) {
      if (response.status === 404) throw new Error('Categoria não encontrada');
      throw new Error('Falha ao carregar categoria');
    }
    const data: ApiResponse<ProductCategory> = await response.json();
    return data.data!;
  }

  async createCategory(payload: ProductCategoryFormData): Promise<ProductCategory> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.post('/product-categories', payload);
    if (!response.ok) {
      const err = await response.json().catch(() => null);
      throw new Error(err?.message || 'Falha ao criar categoria');
    }
    const data: ApiResponse<ProductCategory> = await response.json();
    return data.data!;
  }

  async updateCategory(id: number, payload: Partial<ProductCategoryFormData>): Promise<ProductCategory> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.put(`/product-categories/${id}`, payload);
    if (!response.ok) {
      const err = await response.json().catch(() => null);
      throw new Error(err?.message || 'Falha ao atualizar categoria');
    }
    const data: ApiResponse<ProductCategory> = await response.json();
    return data.data!;
  }

  async deleteCategory(id: number): Promise<void> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.delete(`/product-categories/${id}`);
    if (!response.ok) {
      throw new Error('Falha ao excluir categoria');
    }
  }

  /** List products associated with a category. */
  async listCategoryProducts(
    id: number,
    page = 1,
    pageSize = 50
  ): Promise<{ products: Product[]; pagination: BackendPagination | null }> {
    const params = new URLSearchParams({ page: page.toString(), page_size: pageSize.toString() });
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/product-categories/${id}/products?${params}`);
    if (!response.ok) {
      throw new Error('Falha ao carregar produtos da categoria');
    }
    const json: ListResponse<Product> = await response.json();
    return { products: json.data ?? [], pagination: json.pagination ?? null };
  }

  /** Associate (or move) products to a category. */
  async associateProducts(id: number, productIds: number[]): Promise<void> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.post(`/product-categories/${id}/products`, { product_ids: productIds });
    if (!response.ok) {
      const err = await response.json().catch(() => null);
      throw new Error(err?.message || 'Falha ao associar produtos à categoria');
    }
  }

  /** Remove a product's association (sets the product's category to null). */
  async removeProductAssociation(id: number, productId: number): Promise<void> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.delete(`/product-categories/${id}/products/${productId}`);
    if (!response.ok) {
      throw new Error('Falha ao remover associação do produto');
    }
  }
}

export const productCategoryService = new ProductCategoryService();
export default productCategoryService;
